import { Component, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    // F4.6: Sentry integration (no-op if Sentry not loaded)
    const Sentry = (window as any).Sentry
    if (Sentry?.captureException) {
      Sentry.captureException(error, { contexts: { react: errorInfo } })
    }
    // Also dispatch a custom event for analytics
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:error', { detail: { error, errorInfo } }))
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />
    }
    return this.props.children
  }
}

function ErrorFallback({ error }: { error?: Error }) {
  // Intentionally NOT using useTranslation here to avoid infinite loop
  // if i18n itself throws an error
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <AlertTriangle className="w-16 h-16 text-destructive mx-auto mb-6" />
        <h1 className="font-display text-2xl font-bold mb-4">Something went wrong</h1>
        <p className="text-muted-foreground mb-6">
          An unexpected error occurred. Please try refreshing the page.
        </p>
        {error && (
          <p className="text-xs text-muted-foreground mb-6 bg-secondary p-3 rounded-lg">
            {error.message}
          </p>
        )}
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-6 py-2 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            Reload Page
          </button>
          <Link
            to="/"
            className="px-6 py-2 rounded-full bg-secondary text-foreground font-medium hover:bg-secondary/80 transition-colors"
          >
            Go Home
          </Link>
        </div>
      </div>
    </div>
  )
}

export default ErrorBoundaryClass
